import React, { useState, useCallback } from "react";
import {
  Box,
  useTheme,
  Paper,
  Pagination,
  CircularProgress,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import { KylasDeal } from "../../../models/Dashboard/SalesDashboard/Deals";
import { KylasLead } from "../../../models/Dashboard/SalesDashboard/Leads";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import Popover from "@mui/material/Popover";
import PipelineItemDetailsCard from "../analytics/PipelineItemDetailsCard";

interface ColumnDataProps {
  loading?: boolean;
  heading: string;
  amount: string;
  list?: (KylasLead | KylasDeal)[];
  cardId: string;
  pageNo: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (newPage: number) => void;
  totalCount: number;
  type: "lead" | "deal";
}

const ColumnData: React.FC<ColumnDataProps> = ({
  loading = false,
  heading,
  amount,
  list = [],
  cardId,
  pageNo,
  totalPages,
  itemsPerPage = 50,
  onPageChange,
  totalCount,
  type,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [clickedItem, setClickedItem] = useState<KylasLead | KylasDeal | null>(
    null
  );

  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, item: KylasLead | KylasDeal) => {
      setAnchorEl(event.currentTarget);
      setClickedItem(item);
    },
    []
  );

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
    setClickedItem(null);
  }, []);

  const handlePageChange = useCallback(
    (_: any, newPage: number) => {
      onPageChange(newPage);
    },
    [onPageChange]
  );

  const formatLeadData = useCallback((lead: KylasLead) => {
    const lastActivity = lead.latest_activity_created_at
      ? dayjs(lead.latest_activity_created_at)
      : null;

    const daysSinceLastActivity = lastActivity
      ? dayjs().diff(lastActivity, "day")
      : "-";

    const ownerInitials =
      lead.owner_name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase() || "";

    const sourceName = lead.source_name || "";
    const companyName = lead.company_name || "";

    const totalAmount = Number(lead.total_amount);
    const discount = Number(lead.discount);
    const amountBeforeDiscount = Number(lead.amount_before_discount);
    const hasOrder = !!lead.order_id;

    let discountPercentage: string | null = null;
    if (
      hasOrder &&
      Number.isFinite(amountBeforeDiscount) &&
      Number.isFinite(discount)
    ) {
      const totalBeforeDiscount = amountBeforeDiscount || 0;
      if (totalBeforeDiscount > 0) {
        const pct = (discount / totalBeforeDiscount) * 100;
        discountPercentage = `${pct.toFixed(2)}%`;
      }
    }

    const amount =
      Number.isFinite(totalAmount) && totalAmount > 0
        ? `₹${totalAmount}${
            discountPercentage ? ` (${discountPercentage})` : ""
          }`
        : lead.requirement_budget
        ? `₹${lead.requirement_budget}`
        : "";

    const totalDays = lead.created_at
      ? dayjs().diff(dayjs(lead.created_at), "day")
      : "-";

    return [
      `(LA:${daysSinceLastActivity})`,
      ownerInitials ? `(${ownerInitials})` : "",
      sourceName,
      hasOrder ? lead.order_id : "",
      companyName,
      amount,
      totalDays !== "-" ? `(${totalDays})` : "",
      lead.id,
    ]
      .filter(Boolean)
      .join("-");
  }, []);

  const formatDealData = useCallback((deal: KylasDeal) => {
    const ownerInitials =
      deal.owner_name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase() || "";

    const companyName = deal.company_name || "";
    const totalAmount = Number(deal.total_amount);
    const orderAmount = Number(deal.amount_before_discount);
    const discount = Number(deal.discount);
    const hasOrder = !!deal.order_id;

    let discountPercentage: string | null = null;
    if (hasOrder && Number.isFinite(orderAmount) && Number.isFinite(discount)) {
      const totalBeforeDiscount = orderAmount || 0;
      if (totalBeforeDiscount > 0) {
        const pct = (discount / totalBeforeDiscount) * 100;
        discountPercentage = `${pct.toFixed(2)}%`;
      }
    }

    const amount =
      Number.isFinite(totalAmount) && totalAmount > 0
        ? `₹${totalAmount}${
            discountPercentage ? ` (${discountPercentage})` : ""
          }`
        : deal.estimated_currency_value
        ? `₹${deal.estimated_currency_value}`
        : "";

    const totalDays = deal.created_at
      ? dayjs().diff(dayjs(deal.created_at), "day")
      : "-";

    return [
      ownerInitials ? `(${ownerInitials})` : "",
      hasOrder ? deal.order_id : "",
      deal.name || "",
      companyName,
      amount,
      totalDays !== "-" ? `(${totalDays})` : "",
      deal.id,
    ]
      .filter(Boolean)
      .join("-");
  }, []);

  const formatItemData = useCallback(
    (item: KylasLead | KylasDeal) => {
      return type === "lead"
        ? formatLeadData(item as KylasLead)
        : formatDealData(item as KylasDeal);
    },
    [type, formatLeadData, formatDealData]
  );

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          sx={{
            mt: 2,
            pb: 2,
            minWidth: 300,
            boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
          }}
          id={cardId}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{
              padding: 1,
              backgroundColor:
                theme.palette.customColors?.lightGreyColor || "#f5f5f5",
            }}
          >
            <Box sx={{ marginLeft: 1.6 }}>
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.greyTextColor}
                style={{ fontWeight: "500", paddingBottom: 5 }}
              >
                {heading}
              </CustomTypography>
              <CustomTypography variant="heading4">{amount}</CustomTypography>
            </Box>
          </Box>

          <Box
            sx={{
              maxHeight: "400px",
              minHeight: "400px",
              overflowY: "auto",
              px: 2.4,
              "&::-webkit-scrollbar": {
                width: "3px",
              },
              "&::-webkit-scrollbar-track": {
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: "3px",
              },
            }}
          >
            {list.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: theme.palette.text.secondary,
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                No Data
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {list.map((item, index) => (
                  <Box
                    key={`${cardId}-${index}`}
                    sx={{
                      p: 1,
                      fontSize: "12px",
                      textAlign: "center",
                      backgroundColor:
                        type === "lead"
                          ? theme.palette.customColors.purpleMain
                          : theme.palette.customColors.lightOrange,
                      color:
                        type === "lead"
                          ? theme.palette.customColors.white
                          : theme.palette.customColors.blackBorder,
                      cursor: "pointer",
                    }}
                    onClick={(e) => handlePopoverOpen(e, item)}
                  >
                    {formatItemData(item)}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box mt={2} textAlign="center">
            <CustomTypography variant="body2">
              Showing {(pageNo - 1) * itemsPerPage + 1}–
              {Math.min(pageNo * itemsPerPage, totalCount)} / {totalCount}
            </CustomTypography>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={pageNo}
                onChange={handlePageChange}
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            disableRestoreFocus
            sx={{ pointerEvents: "auto" }}
          >
            {clickedItem && (
              <PipelineItemDetailsCard
                type={type}
                heading={heading}
                lastActive={
                  clickedItem.latest_activity_created_at
                    ? `${dayjs().diff(
                        dayjs(clickedItem.latest_activity_created_at),
                        "day"
                      )} days ago`
                    : "-"
                }
                owner={
                  clickedItem.owner_name ? String(clickedItem.owner_name) : "-"
                }
                company={
                  clickedItem.company_name
                    ? String(clickedItem.company_name)
                    : "-"
                }
                source={
                  (clickedItem as KylasLead).source_name &&
                  (clickedItem as KylasLead).source_name !== ""
                    ? String((clickedItem as KylasLead).source_name)
                    : "-"
                }
                orderId={
                  clickedItem.order_id ? `#${clickedItem.order_id}` : "-"
                }
                value={
                  clickedItem.total_amount
                    ? `₹${clickedItem.total_amount}`
                    : (clickedItem as KylasLead).requirement_budget
                    ? `₹${(clickedItem as KylasLead).requirement_budget}`
                    : (clickedItem as KylasDeal).estimated_currency_value
                    ? `₹${(clickedItem as KylasDeal).estimated_currency_value}`
                    : "-"
                }
                firstActive={
                  clickedItem.created_at
                    ? `${dayjs().diff(
                        dayjs(clickedItem.created_at),
                        "day"
                      )} days ago`
                    : "-"
                }
                discount={""}
              />
            )}
          </Popover>
        </Paper>
      )}
    </>
  );
};

export default observer(ColumnData);
